import React, { useContext, useEffect, useState } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { SecondaryButton, ButtonIcon, MonetaryTextField } from '@hz-design-system/web-ui';

import OneStepInfoBidding from './OneStepInfoBidding';
import { isValidAmountInputValue } from '../../../../utils/isValidAmountInputValue';
import getBids from '../../repositories/getBids';
import { trackGAEvent } from '../../../../utils/gaTracking';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import { getHashParamValue, deleteAllHashParams } from '../../../../utils/hashParams';

import { TListingClient } from 'types/TListing';
import { TBiddingClient } from 'types/TBidding';

import Classes from './OneStepBidding.module.scss';

let OneStepBiddingMessageDialog;
let ConfirmDeleteBidModal;

const BID_HASH_PARAMS = {
  ACTION: { KEY: 'action', VALUE: 'showBidding' },
};

type TOneStepBiddingProps = {
  itemId: TListingClient['itemId'];
  priceType: TListingClient['priceInfo']['priceType'];
  adBids: TListingClient['bidsInfo']['bids'];
  onUpdateBidsHandler: (bids: TListingClient['bidsInfo']['bids']) => void;
  title: TListingClient['title'];
  currentMinimumBid?: TListingClient['bidsInfo']['currentMinimumBid'];
  sellerType: TListingClient['seller']['sellerType'];
  withPhoneNumber?: boolean;
  isReserved: TListingClient['isReserved'];
  type?: 'INFO' | 'BID';
};

let LoginDialogContainer;

const OneStepBidding = ({
  priceType,
  itemId,
  title,
  currentMinimumBid,
  sellerType,
  withPhoneNumber,
  type,
  adBids,
  isReserved,
  onUpdateBidsHandler,
}: TOneStepBiddingProps) => {
  const { t } = useI18nContext();
  const [amountValue, setAmountValue] = useState<string>('');
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valid = isValidAmountInputValue(e.target.value);
    if (valid) {
      setAmountValue(e.target.value);
    } else {
      e.preventDefault();
    }
  };

  const {
    userLoggedIn,
    xsrfToken,
    userDetails: { isAdOwner = false, id: loggedUserId } = {},
  } = useContext(EnvironmentContext);
  const [bidToDelete, setBidToDelete] = useState(undefined);

  const [biddingMessageDialogLoaded, setBiddingMessageDialogLoaded] = useState(false);
  const [confirmDeleteBidModalLoaded, setConfirmDeleteBidModalLoaded] = useState(false);
  const [showBiddingMessageDialog, setShowBiddingMessageDialog] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showConfirmDeleteBidModal, setShowConfirmDeleteBidModal] = useState(false);

  const fetchBids = async () => {
    const response: TBiddingClient = await getBids({ itemId, xsrfToken });
    if (response.bids) {
      onUpdateBidsHandler(response.bids);
    }
  };

  const loadBiddingMessageDialog = async () => {
    if (!biddingMessageDialogLoaded) {
      try {
        OneStepBiddingMessageDialog = (
          await import(/* webpackChunkName: "BiddingMessageDialog" */ './OneStepBiddingMessageDialog')
        ).default;
        setBiddingMessageDialogLoaded(true);
      } catch {}
    }
  };

  const handleShowBiddingHash = () => {
    if (getHashParamValue(BID_HASH_PARAMS.ACTION.KEY) === BID_HASH_PARAMS.ACTION.VALUE) {
      deleteAllHashParams();
      handleBiddingClick();
    }
  };

  // after user login we have to re-load page and show bidding dialog if he clicked on "place a bid" button before login
  useEffect(() => {
    if (localStorage && localStorage.getItem('continueAfterReload') === 'true') {
      if (!isReserved) {
        trackGAEvent('R2SBidBegin');
      }
      localStorage.removeItem('continueAfterReload');
      (async () => {
        try {
          await loadBiddingMessageDialog();
          setShowBiddingMessageDialog(true);
        } catch {}
      })();
    }

    handleShowBiddingHash();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfirmDeleteBidModal = async () => {
    if (!confirmDeleteBidModalLoaded) {
      try {
        ConfirmDeleteBidModal = (
          await import(/* webpackChunkName: "ConfirmDeleteBidModal" */ './ConfirmDeleteBidModal')
        ).default;
        setConfirmDeleteBidModalLoaded(true);
      } catch {}
    }
  };

  const handleCloseBiddingDialogClick = () => {
    setShowBiddingMessageDialog(false);
    setAmountValue('');
  };

  const handleBiddingClick = async () => {
    if (userLoggedIn) {
      if (!isReserved) {
        trackGAEvent('R2SBidBegin');
      }
      await loadBiddingMessageDialog();
      setShowBiddingMessageDialog(true);
    } else {
      LoginDialogContainer = (
        await import(
          /* webpackChunkName: "loginDialogContainer" */ '../../../../components/LoginDialog/LoginDialogContainer'
        )
      ).default;
      setShowLoginDialog(true);
    }
  };

  const handleLoginSuccess = () => {
    if (!userLoggedIn) {
      localStorage.setItem('continueAfterReload', 'true');
      document.location.reload();
    }
  };

  const handleShowConfirmDeleteBidModal = async (bid) => {
    await loadConfirmDeleteBidModal();
    setBidToDelete(bid);
    setShowBiddingMessageDialog(false);
    setShowConfirmDeleteBidModal(true);
  };

  const handleBackConfirmDeleteBidModal = () => {
    setBidToDelete(undefined);
    setShowBiddingMessageDialog(true);
    setShowConfirmDeleteBidModal(false);
  };

  const handleCancelConfirmDeleteBidModal = () => {
    setBidToDelete(undefined);
    setShowConfirmDeleteBidModal(false);
  };

  const handleSuccessBidsUpdate = async () => {
    setBidToDelete(undefined);
    setAmountValue('');
    await fetchBids();
  };

  const handleSuccessBidsDelete = async () => {
    await fetchBids();
    handleBackConfirmDeleteBidModal();
  };

  if (isAdOwner) {
    return null;
  }

  return (
    <div className={Classes.root}>
      {type === 'INFO' ? (
        <OneStepInfoBidding bids={adBids} loggedUserId={loggedUserId} onClickHandler={handleBiddingClick} />
      ) : (
        !isReserved && (
          <div className={Classes.bidControls}>
            <div className={Classes.bidControl}>
              <MonetaryTextField type="number" onChange={onChange} value={amountValue} />
            </div>
            <div className={Classes.bidControl}>
              <SecondaryButton className={Classes.bidButton} onClick={handleBiddingClick}>
                <ButtonIcon name="placebid" isStartIcon={false} />
                {t('bidding.addBid.button.label')}
              </SecondaryButton>
            </div>
          </div>
        )
      )}

      {biddingMessageDialogLoaded && showBiddingMessageDialog && (
        <OneStepBiddingMessageDialog
          bids={adBids}
          title={title}
          currentMinimumBid={currentMinimumBid}
          itemId={itemId}
          sellerType={sellerType}
          onClose={handleCloseBiddingDialogClick}
          withPhoneNumber={withPhoneNumber}
          onDeleteBidHandler={handleShowConfirmDeleteBidModal}
          onSuccessBidsUpdate={handleSuccessBidsUpdate}
          isReserved={isReserved}
          initialAmount={amountValue?.replace(',', '.')}
        />
      )}

      {confirmDeleteBidModalLoaded && showConfirmDeleteBidModal && (
        <ConfirmDeleteBidModal
          onConfirm={handleSuccessBidsDelete}
          onBack={handleBackConfirmDeleteBidModal}
          onCancel={handleCancelConfirmDeleteBidModal}
          bid={bidToDelete}
        />
      )}

      {showLoginDialog && <LoginDialogContainer onClose={setShowLoginDialog} onSuccess={handleLoginSuccess} />}
    </div>
  );
};

export default OneStepBidding;
